import React, { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  History,
  Loader2,
  Pencil,
  Printer,
  Search,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { aed } from "@/lib/format";
import { cn } from "@/lib/utils";

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function formatPaidAt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Paginated POS bill history with date / search / type filters.
 * Edit (open in POS) only when bill business day matches open POS day.
 */
export default function SimpleBillHistorySheet({
  open,
  onClose,
  branchId,
  openBusinessDate,
  onReprint,
  onEdit,
  reprintPending,
  t,
}) {
  const [dateFrom, setDateFrom] = useState(openBusinessDate || todayYmd());
  const [dateTo, setDateTo] = useState(openBusinessDate || todayYmd());
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [orderType, setOrderType] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!open) return;
    const day = openBusinessDate || todayYmd();
    setDateFrom(day);
    setDateTo(day);
    setSearch("");
    setDebouncedSearch("");
    setOrderType("all");
    setPage(1);
  }, [open, openBusinessDate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["simple-pos-bill-history", branchId, dateFrom, dateTo, debouncedSearch, orderType, page],
    queryFn: async () => {
      const { data: res } = await api.get("/bills/history", {
        params: {
          branch_id: branchId,
          date_from: dateFrom,
          date_to: dateTo,
          search: debouncedSearch || undefined,
          order_type: orderType !== "all" ? orderType : undefined,
          page,
          per_page: 15,
        },
      });
      return res;
    },
    enabled: Boolean(open && branchId && dateFrom && dateTo),
    placeholderData: keepPreviousData,
  });

  if (!open) return null;

  const bills = data?.bills ?? [];
  const meta = data?.meta || { page: 1, last_page: 1, total: 0 };
  const posDay = openBusinessDate || todayYmd();

  const canEditBill = (b) => {
    const biz = String(b.business_date || (b.paid_at || "").slice(0, 10) || "");
    return Boolean(posDay && biz && biz === posDay);
  };

  return (
    <div
      className="fixed inset-0 z-[210] grid place-items-end bg-black/45 sm:place-items-center"
      onClick={onClose}
      data-testid="simple-pos-history"
    >
      <div
        className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-3xl bg-white sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 border-b px-4 py-3">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
              <History className="h-5 w-5 text-violet-600" />
              {t("simple.posHistoryTitle", "Invoice history")}
            </h3>
            <p className="mt-0.5 text-[12px] text-slate-500">
              {t("simple.posHistoryHint", "Filter by date. Edit only for today’s POS day.")}
            </p>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-2 border-b px-4 py-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="mb-1 block text-[11px]">{t("simple.posHistoryFrom", "From")}</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className="h-10"
                data-testid="simple-pos-history-from"
              />
            </div>
            <div>
              <Label className="mb-1 block text-[11px]">{t("simple.posHistoryTo", "To")}</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                className="h-10"
                data-testid="simple-pos-history-to"
              />
            </div>
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("simple.posHistorySearch", "Bill #, customer…")}
              className="h-10 ps-9"
              data-testid="simple-pos-history-search"
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-0.5">
            {[
              { id: "all", label: t("simple.posAll", "All") },
              { id: "DI", label: t("pos.dineIn", "Dine In") },
              { id: "TA", label: t("pos.takeAway", "Take Away") },
              { id: "DL", label: t("pos.delivery", "Delivery") },
            ].map((ot) => (
              <button
                key={ot.id}
                type="button"
                onClick={() => {
                  setOrderType(ot.id);
                  setPage(1);
                }}
                className={cn(
                  "shrink-0 rounded-full px-3 py-1.5 text-xs font-bold",
                  orderType === ot.id ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600",
                )}
              >
                {ot.label}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {(isLoading || isFetching) && bills.length === 0 ? (
            <div className="grid place-items-center gap-2 py-16 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t("common.loading")}
            </div>
          ) : null}
          {!isLoading && bills.length === 0 ? (
            <p className="px-4 py-12 text-center text-sm text-slate-400">
              {t("simple.posHistoryEmpty", "No invoices in this range")}
            </p>
          ) : null}
          <ul>
            {bills.map((b) => {
              const editable = canEditBill(b);
              return (
                <li key={b.id} className="border-b border-slate-100 px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-extrabold text-violet-700">{b.bill_number}</div>
                      <div className="truncate text-[12px] text-slate-600">
                        {[b.order_type_label, b.customer_name, formatPaidAt(b.paid_at)].filter(Boolean).join(" · ")}
                      </div>
                      {!editable ? (
                        <div className="mt-0.5 text-[10px] font-semibold uppercase text-slate-400">
                          {t("simple.posHistoryViewOnly", "Other day — reprint only")}
                        </div>
                      ) : null}
                    </div>
                    <div className="font-mono text-sm font-bold text-slate-900">{aed(b.net_amount)}</div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    {editable ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-9 flex-1 font-bold"
                        onClick={() => onEdit(b)}
                        data-testid={`simple-pos-history-edit-${b.id}`}
                      >
                        <Pencil className="me-1.5 h-3.5 w-3.5" />
                        {t("simple.posHistoryEdit", "Open / Edit")}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      className="h-9 flex-1 bg-violet-600 font-bold hover:bg-violet-700"
                      disabled={reprintPending}
                      onClick={() => onReprint(b)}
                      data-testid={`simple-pos-history-reprint-${b.id}`}
                    >
                      {reprintPending ? <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" /> : <Printer className="me-1.5 h-3.5 w-3.5" />}
                      {t("simple.posHistoryReprint", "Reprint")}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex items-center justify-between gap-2 border-t px-4 py-3">
          <button
            type="button"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="inline-flex h-10 items-center gap-1 rounded-xl bg-slate-100 px-3 text-sm font-bold text-slate-700 disabled:opacity-40"
            data-testid="simple-pos-history-prev-page"
          >
            <ChevronLeft className="h-4 w-4" />
            {t("common.prev", "Prev")}
          </button>
          <div className="text-center text-[12px] font-semibold text-slate-500">
            {t("simple.posHistoryPage", "Page {{page}} / {{last}} · {{total}}", {
              page: meta.page || page,
              last: meta.last_page || 1,
              total: meta.total || 0,
            })}
          </div>
          <button
            type="button"
            disabled={page >= (meta.last_page || 1) || isFetching}
            onClick={() => setPage((p) => p + 1)}
            className="inline-flex h-10 items-center gap-1 rounded-xl bg-slate-100 px-3 text-sm font-bold text-slate-700 disabled:opacity-40"
            data-testid="simple-pos-history-next-page"
          >
            {t("common.next", "Next")}
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
